import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import CapturePage from "./pages/CapturePage";
import IngredientsPage from "./pages/IngredientsPage";
import RecipesPage from "./pages/RecipesPage";
import RecipeEditor from "./pages/RecipeEditor";
import QuotePage from "./pages/QuotePage";
import InvoicesPage from "./pages/InvoicesPage";
import InvoicePaymentResultPage from "./pages/InvoicePaymentResultPage";
import PricingPage from "./pages/PricingPage";
import SettingsPage from "./pages/SettingsPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import BillingCancelPage from "./pages/BillingCancelPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PriceFloorCalculator from "./pages/PriceFloorCalculator";
import LandingPage from "./pages/LandingPage";
import LabourGuidePage from "./pages/LabourGuidePage";
import YieldGuidePage from "./pages/YieldGuidePage";
import { RouteEffects } from "./components/RouteEffects";

const ENTITLED_STATUSES = new Set(["trialing", "active", "manual", "past_due"]);

function Protected({ children, allowUnentitled = false }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen bg-cream flex items-center justify-center text-ink/40 font-body">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!allowUnentitled && !ENTITLED_STATUSES.has(user.subscription_status)) return <Navigate to="/pricing" replace />;
  return <Layout>{children}</Layout>;
}

function HomeRoute() {
  const { user } = useAuth();
  // Wait for /auth/me before choosing a screen, so a logged-in visit does not
  // flash the public landing. Logged-out visitors get the landing; everyone
  // else keeps the previous gate (dashboard, or /pricing if not entitled).
  if (user === false) return <LandingPage />;
  return (
    <Protected>
      <Dashboard />
    </Protected>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/what-should-i-charge" element={<PriceFloorCalculator />} />
      {/* public — the person paying an invoice is the caterer's client, not a Mnandi account holder */}
      <Route path="/invoice-paid" element={<InvoicePaymentResultPage paid />} />
      <Route path="/invoice-cancelled" element={<InvoicePaymentResultPage />} />
      <Route path="/learn/labour-costing" element={<LabourGuidePage />} />
      <Route path="/learn/yield" element={<YieldGuidePage />} />
      <Route path="/pricing" element={<Protected allowUnentitled><PricingPage /></Protected>} />
      <Route path="/settings" element={<Protected allowUnentitled><SettingsPage /></Protected>} />
      <Route path="/billing/success" element={<Protected allowUnentitled><BillingSuccessPage /></Protected>} />
      <Route path="/billing/cancel" element={<Protected allowUnentitled><BillingCancelPage /></Protected>} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/capture" element={<Protected><CapturePage /></Protected>} />
      <Route path="/ingredients" element={<Protected><IngredientsPage /></Protected>} />
      <Route path="/recipes" element={<Protected><RecipesPage /></Protected>} />
      <Route path="/recipes/new" element={<Protected><RecipeEditor /></Protected>} />
      <Route path="/recipes/:id" element={<Protected><RecipeEditor /></Protected>} />
      <Route path="/quote" element={<Protected><QuotePage /></Protected>} />
      {/* allowUnentitled: invoicing is free (capped) as an acquisition wedge — see memory/PRD.md */}
      <Route path="/invoices" element={<Protected allowUnentitled><InvoicesPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteEffects />
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
